import { z } from "zod";
import {
  JiraConfig,
  JiraConfigCreate,
  JiraEffectiveConfig,
  JiraProject,
  JiraTeamConfigUpdate,
} from "../schemas/jiraConfig";
import { request, validateInput } from "../request";
import type { ActionReturnType } from "../types";

export class JiraConfigsEndpoint {
  public static readonly basePath = "jira-configs";
  public static readonly schemas = {
    list: {
      query: z.object({
        limit: z.number().int().positive().optional(),
        offset: z.number().int().nonnegative().optional(),
      }),
      response: z.object({ count: z.number().int(), items: JiraConfig.array() }),
    },
    read: { params: z.object({ config_id: z.number().int().positive() }), response: JiraConfig },
    create: { body: JiraConfigCreate, response: JiraConfig },
    update: { body: JiraTeamConfigUpdate, response: JiraConfig },
    effective: {
      query: z.object({ project_id: z.number().int().positive() }),
      response: JiraEffectiveConfig,
    },
    projects: {
      query: z.object({ project_id: z.number().int().positive().optional(), query: z.string().trim().optional() }),
      response: JiraProject.array(),
    },
    updateProject: {
      params: z.object({ config_id: z.number().int().positive() }),
      body: z.object({ project_key: z.string().trim().min(1) }),
      response: JiraConfig,
    },
  };

  public list(query: z.input<typeof JiraConfigsEndpoint.schemas.list.query> = {}) {
    const valid = validateInput(JiraConfigsEndpoint.schemas.list.query, query);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraConfigs", "list"],
      method: "get",
      path: [JiraConfigsEndpoint.basePath],
      query: valid.data,
      localErrorHandling: true,
      response: JiraConfigsEndpoint.schemas.list.response,
    });
  }

  public read(configId: number) {
    const valid = validateInput(JiraConfigsEndpoint.schemas.read.params, { config_id: configId });
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraConfigs", "read"],
      method: "get",
      path: [JiraConfigsEndpoint.basePath, valid.data.config_id],
      localErrorHandling: true,
      response: JiraConfig,
    });
  }

  public create(body: z.input<typeof JiraConfigCreate>) {
    const valid = validateInput(JiraConfigCreate, body);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraConfigs", "create"],
      method: "post",
      path: [JiraConfigsEndpoint.basePath],
      json: valid.data,
      localErrorHandling: true,
      response: JiraConfig,
    });
  }

  public update(configId: number, body: z.input<typeof JiraTeamConfigUpdate>) {
    const params = validateInput(JiraConfigsEndpoint.schemas.read.params, { config_id: configId });
    if (!params.ok) return Promise.resolve(params);
    const valid = validateInput(JiraTeamConfigUpdate, body);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraConfigs", "update"],
      method: "patch",
      path: [JiraConfigsEndpoint.basePath, params.data.config_id],
      json: valid.data,
      localErrorHandling: true,
      response: JiraConfig,
    });
  }

  public effective(
    query: z.input<typeof JiraConfigsEndpoint.schemas.effective.query>,
  ): ActionReturnType<z.infer<typeof JiraEffectiveConfig>> {
    const valid = validateInput(JiraConfigsEndpoint.schemas.effective.query, query);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraConfigs", "effective"],
      method: "get",
      path: [JiraConfigsEndpoint.basePath, "effective"],
      query: valid.data,
      localErrorHandling: true,
      response: JiraEffectiveConfig,
    });
  }

  public projects(
    query: z.input<typeof JiraConfigsEndpoint.schemas.projects.query>,
  ): ActionReturnType<z.infer<typeof JiraProject>[]> {
    const valid = validateInput(JiraConfigsEndpoint.schemas.projects.query, query);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraConfigs", "projects"],
      method: "get",
      path: [JiraConfigsEndpoint.basePath, "projects"],
      query: valid.data,
      localErrorHandling: true,
      response: JiraConfigsEndpoint.schemas.projects.response,
    });
  }

  public updateProject(
    configId: number,
    body: z.input<typeof JiraConfigsEndpoint.schemas.updateProject.body>,
  ): ActionReturnType<z.infer<typeof JiraConfig>> {
    const params = validateInput(JiraConfigsEndpoint.schemas.updateProject.params, { config_id: configId });
    if (!params.ok) return Promise.resolve(params);
    const valid = validateInput(JiraConfigsEndpoint.schemas.updateProject.body, body);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraConfigs", "updateProject"],
      method: "patch",
      path: [JiraConfigsEndpoint.basePath, params.data.config_id],
      json: valid.data,
      localErrorHandling: true,
      response: JiraConfig,
    });
  }
}
